import { Injectable } from "@angular/core";
import { Observable, catchError, of, shareReplay } from "rxjs";
import {
	CreateCups,
	CupsViewModel,
	EnergiaCatalogos,
	EnergiaContrato,
	EnergiaResumen,
	ImportacionCupsFila,
	ImportacionCupsResultado,
	UpdateCups,
} from "../models/energia.model";
import { ApiEnum, HttpDataService } from "./http-data.service";

/**
 * Gestión energética (CUPS). Lectura por centro/contrato = visibilidad de la entidad
 * (el back devuelve 404 fuera de alcance → se degrada a []/null sin romper la vista).
 * Escritura gateada en back: App.Energia.Manage O edición del centro. El resumen global
 * y el import masivo son solo para gestores de energía (/energy).
 */
@Injectable({ providedIn: "root" })
export class EnergiaService {
	/** Catálogos (semilla local en back; futuro proxy API DAMA): cache de sesión. */
	private catalogos$?: Observable<EnergiaCatalogos>;

	constructor(private readonly httpDataService: HttpDataService) {}

	getCupsByCentro(centroId: number): Observable<CupsViewModel[]> {
		return this.httpDataService
			.get$<CupsViewModel[]>(ApiEnum.API_ROOT, `/Cups/Centro/${centroId}`)
			.pipe(catchError(() => of<CupsViewModel[]>([])));
	}

	createCups(cups: CreateCups): Observable<{ id: number }> {
		return this.httpDataService.post$<{ id: number }>(ApiEnum.API_ROOT, "/Cups", cups);
	}

	updateCups(cups: UpdateCups): Observable<{ id: number }> {
		return this.httpDataService.put$<{ id: number }>(ApiEnum.API_ROOT, `/Cups/${cups.id}`, cups);
	}

	deleteCups(id: number): Observable<{ id: number }> {
		return this.httpDataService.delete$<{ id: number }>(ApiEnum.API_ROOT, `/Cups/${id}`);
	}

	getEnergiaContrato(contratoId: number): Observable<EnergiaContrato | null> {
		return this.httpDataService
			.get$<EnergiaContrato>(ApiEnum.API_ROOT, `/Energia/Contrato/${contratoId}`)
			.pipe(catchError(() => of(null)));
	}

	getResumen(): Observable<EnergiaResumen | null> {
		return this.httpDataService
			.get$<EnergiaResumen>(ApiEnum.API_ROOT, "/Energia/Resumen")
			.pipe(catchError(() => of(null)));
	}

	getCatalogos(): Observable<EnergiaCatalogos> {
		this.catalogos$ ??= this.httpDataService
			.get$<EnergiaCatalogos>(ApiEnum.API_ROOT, "/Energia/Catalogos")
			.pipe(
				catchError(() => of<EnergiaCatalogos>({ estados: [], tarifas: [], lotes: [] })),
				shareReplay(1),
			);
		return this.catalogos$;
	}

	importacionMasiva(filas: ImportacionCupsFila[], dryRun: boolean): Observable<ImportacionCupsResultado> {
		return this.httpDataService.post$<ImportacionCupsResultado>(
			ApiEnum.API_ROOT,
			`/Cups/ImportacionMasiva?dryRun=${dryRun}`,
			filas,
		);
	}
}
